import { useCallback, useEffect, useRef, useState } from 'react';
import { isPermissionDenied } from 'utils/apiUtils';
import { useLazyGetJobExpensesQuery } from '../../api';
import { JobExpense } from '../../jobs.model';
import { selectCurrentTeam, selectCurrentTeamAccountingPermissions } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { APIErrorStatuses } from 'services/type';

export interface JobExpensesQueryParams {
  type?: string;
}

export interface JobExpensesSummary {
  teamExpenses: string;
  companyExpenses: string;
  total: string;
}

export const initialExpensesQueryParamsState: JobExpensesQueryParams = {
  type: null
};

interface UsePaginatedJobExpensesQueryProps {
  jobId: string;
}

export const usePaginatedJobExpensesQuery = ({ jobId }: UsePaginatedJobExpensesQueryProps) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentTeamAccountingPermissions = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { manageOthersExpenses, createExpenses } = currentTeamAccountingPermissions ?? {};
  const [list, setList] = useState<JobExpense[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalListCount, setTotalListCount] = useState<number>(0);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);
  const [queryParams, setQueryParams] = useState<JobExpensesQueryParams>(initialExpensesQueryParamsState);
  const [isMounted, setIsMounted] = useState(false);
  const [summary, setSummary] = useState<JobExpensesSummary | null>(null);

  const [getExpensesQuery, getExpensesState] = useLazyGetJobExpensesQuery();
  const {
    isFetching: isGetExpensesFetching,
    isSuccess: isGetExpensesSuccess,
    isError: isGetExpensesError,
    error: getExpensesErrorState,
    data: expensesData
  } = getExpensesState;

  const page = useRef(0);
  const isInitListCall: boolean = page.current === 0;

  useEffect(() => {
    if (!isGetExpensesFetching && isGetExpensesSuccess) {
      const expenses = expensesData?.data?.expenses ?? [];
      const updatedList = isInitListCall ? expenses : [...list, ...expenses];
      const expensesLeftToFetch = expensesData?.data?.count ? +expensesData?.data?.count - updatedList.length : 0;
      setHasMore(expensesLeftToFetch > 0);
      setList(updatedList);
      if (page.current === 1) {
        setTotalListCount(expensesData?.data?.count ?? 0);
      }
      setSummary({
        teamExpenses: expensesData?.data?.teamExpenseAmount ?? '0',
        companyExpenses: expensesData?.data?.companyExpenseAmount ?? '0',
        total: expensesData?.data?.totalExpenseAmount ?? '0'
      });
      setIsLoading(false);
    }
  }, [isGetExpensesFetching]);

  useEffect(() => {
    if (!isGetExpensesFetching && isGetExpensesError) {
      setIsError(true);
      setIsLoading(false);
      setHasMore(false);
      if (isPermissionDenied(0 as APIErrorStatuses, getExpensesErrorState?.['data']?.message)) {
        setHasPermissionsToView(false);
      }
    }
  }, [isGetExpensesError]);

  const resetListAndLoadMore = () => {
    page.current = 0;
    setList([]);
    loadMore();
  };

  useEffect(() => {
    if (isMounted) {
      resetListAndLoadMore();
    } else {
      setIsMounted(true);
    }
  }, [queryParams]);

  const loadMore = useCallback(() => {
    setIsLoading(true);
    if (currentTeam && jobId) {
      try {
        getExpensesQuery({
          teamId: currentTeam.id,
          jobId,
          // limit: 50,
          // page: page.current,
          ...queryParams
        }).unwrap();
      } catch (err) {
        setIsError(true);
      }
    }
    // page.current = page.current + 1;
  }, [currentTeam, jobId, page.current, queryParams, manageOthersExpenses, createExpenses]);

  const resetRecallQuery = () => {
    resetListAndLoadMore();
  };

  const resetQuery = () => {
    page.current = 0;
    setList([]);
    setQueryParams(initialExpensesQueryParamsState);
    setIsMounted(false);
    setIsError(false);
    setHasMore(true);
    setTotalListCount(0);
    setSummary(null);
  };

  return {
    isLoading,
    isFetching: isGetExpensesFetching,
    totalListCount,
    list,
    isError,
    loadMore,
    resetQuery,
    resetRecallQuery,
    hasMore,
    isLoadingSuccess: isGetExpensesSuccess,
    hasPermissionsToView,
    setQueryParams,
    queryParams,
    summary
  };
};
